import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, OnDestroy, inject } from '@angular/core';

import { notNil } from 'app/utils/stream-util';

import { HeaderToastMessageComponent } from '../header-toast-message/header-toast-message.component';

@Injectable({
  providedIn: 'root',
})
export class HeaderToastMessageService implements OnDestroy {
  private overlay = inject(Overlay);

  private overlayRef: OverlayRef;
  private portalRef: ComponentPortal<HeaderToastMessageComponent>;
  private ref: ComponentRef<HeaderToastMessageComponent>;

  public constructor() {
    this.overlayRef = this.overlay.create({
      width: '100%',
      height: '0',
      panelClass: 'notification-overlay-pane',
    });
    this.portalRef = new ComponentPortal(HeaderToastMessageComponent);
    this.ref = this.overlayRef.attach(this.portalRef);
  }

  /**
   *
   * @param {BannerOptions} options - The banner options
   * @param {string} buttonText - The text for the button in the notification, if undefined, button won't be displayed
   * @param {string} message - The message to show in the banner
   * @param {number} replace - The id of the banner to be replaced
   * @param action callback function that gets the banner id whose action is being triggered
   * @returns the id of the banner that has been created
   */
  public pushStaticMessage({ buttonText, message, replace }: BannerOptions, action?: (id: number) => void): number {
    if (notNil(replace)) {
      return this.ref.instance.replace({ buttonText, message, action }, replace);
    }
    return this.ref.instance.addBanner({ buttonText, message, action });
  }

  /**
   *
   * @param id The banner's id to remove
   */
  public removeMessage(id: number | undefined): void {
    if (notNil(id)) {
      this.ref.instance.removeBanner(id);
    }
  }

  /**
   * Removes all the displayed banners.
   */
  public removeAllMessages(): void {
    this.ref.instance.removeAll();
  }

  /**
   *
   * @param {BannerOptions} - The options for the banner
   * @param {string} buttonText - The text for the button in the notification, if undefined, button won't be displayed
   * @param {string} message - The message to show in the banner
   * @param {number} durationInSeconds - To indicate for how long do you want to show the banner
   * @param action callback function that gets the banner id whose action is being triggered
   * @returns the id of the banner that has been created
   */
  public pushTemporaryMessage(
    { buttonText, durationInSeconds = 5, message }: BannerOptions,
    action?: (id: number) => void,
  ): number {
    const id = this.ref.instance.addBanner({ buttonText, message, action });
    setTimeout(() => this.removeMessage(id), 1000 * durationInSeconds);
    return id;
  }

  public ngOnDestroy(): void {
    this.ref.destroy();
    this.overlayRef.detach();
  }
}

export interface BannerOptions {
  message: string;
  buttonText?: string;
  action?: (id: number) => void;
  replace?: number;
  durationInSeconds?: number;
}
